import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '../../cn'

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2 sm:px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-w-0 max-w-full overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent shadow bg-accent text-accent-foreground",
        secondary: "border-transparent bg-muted text-muted-foreground",
        danger: "border-transparent shadow bg-danger text-danger-foreground",
        success: "border-transparent shadow bg-success text-success-foreground",
        warn: "border-transparent shadow bg-warn text-warn-foreground",
        info: "border-transparent shadow bg-accent text-accent-foreground",
        outline: "bg-transparent text-foreground border-border",
        /* Full rainbow — chart-palette-backed badge colors.
           text-accent-foreground for dark-bg charts, text-foreground for light-bg (lime, amber). */
        blue: "border-transparent shadow bg-chart-1 text-accent-foreground",
        cyan: "border-transparent shadow bg-chart-2 text-accent-foreground",
        teal: "border-transparent shadow bg-chart-3 text-accent-foreground",
        green: "border-transparent shadow bg-chart-4 text-accent-foreground",
        lime: "border-transparent shadow bg-chart-5 text-foreground",
        amber: "border-transparent shadow bg-chart-6 text-foreground",
        orange: "border-transparent shadow bg-chart-7 text-accent-foreground",
        red: "border-transparent shadow bg-chart-8 text-accent-foreground",
        magenta: "border-transparent shadow bg-chart-9 text-accent-foreground",
        violet: "border-transparent shadow bg-chart-10 text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  const isClickable = !!props.onClick
  return (
    <div
      data-component="Badge"
      data-package="@appic/ui-components"
      role={isClickable ? "button" : undefined}
      className={cn(
        badgeVariants({ variant }),
        isClickable && "cursor-pointer hover:opacity-80 transition-opacity",
        className
      )}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
